interface IWebSocketClient {
  createConnection: (url: string) => WebSocket;
  getSocket: () => WebSocket;
  // send: (data: string | ArrayBuffer | Blob) => void;
  sendJSON: (data: object) => void;
  checkAlive: (timeoutMs: number) => Promise<boolean>;
  onMessage(cb: (event: MessageEvent) => void): void;
  onError(cb: (event: MessageEvent) => void): void;
  onClose(cb: (event: CloseEvent) => void): void;
  onOpen(cb: () => void): void;
}

class WebSocketClient implements IWebSocketClient {

  public static getInstance(): WebSocketClient {
    return WebSocketClient.instance;
  }

  protected static instance = new WebSocketClient;

  private socket: WebSocket;
  private isKilled: boolean;
  private isConnected: boolean;
  private pendingMessages: object[];

  private constructor() {
    this.isKilled = false;
    this.isConnected = false;
    this.pendingMessages = [];
  }

  public createConnection(url: string) {
    this.socket = new WebSocket(url);
    this.isKilled = false;
    this.isConnected = false;
    return this.socket;
  }
  
  public close(clearPending: boolean = false) {
    this.closeSocket(this.socket, clearPending);
  }

  public closeSocket(socketToClose?: WebSocket, clearPending: boolean = false) {
    if (clearPending) {
      this.pendingMessages = [];
    }
    if (!socketToClose) {
      this.isConnected = false;
      return;
    }

    if (socketToClose === this.socket) {
      this.isKilled = true;
      this.isConnected = false;
    }

    if (clearPending) {
      socketToClose.onopen = null;
      socketToClose.onerror = null;
    }
    socketToClose.onmessage = (event: MessageEvent) => {console.log("Received message on closed websocket : "+event.data);};
    socketToClose.onclose = (event: CloseEvent) => {console.log("Closed old websocket gracefully.");};
    if (socketToClose.readyState === socketToClose.OPEN || socketToClose.readyState === socketToClose.CONNECTING) {
      socketToClose.close();
    }
  }

  public setConnected(value: boolean) {
    this.isConnected = value;
    console.log("WebSocket connected: " + value);
    if (value && this.pendingMessages.length > 0) {
      // Flush any queued messages
      for (const msg of this.pendingMessages) {
        this.socket.send(JSON.stringify(msg));
      }
      this.pendingMessages = [];
    }
  }

  public hasConnection(): boolean {
    return this.isConnected;
  }

  public checkAlive(timeoutMs: number): Promise<boolean> {
    const socket = this.socket;

    if (!socket || socket.readyState !== socket.OPEN || !this.isConnected) {
      return Promise.resolve(false);
    }

    return new Promise<boolean>((resolve) => {
      let resolved = false;
      let timeout: number | undefined;

      function finish(alive: boolean) {
        if (resolved) {
          return;
        }

        resolved = true;
        cleanup();
        resolve(alive);
      }

      function cleanup() {
        if (timeout !== undefined) {
          window.clearTimeout(timeout);
        }

        socket.removeEventListener('message', onMessage);
        socket.removeEventListener('close', onClose);
        socket.removeEventListener('error', onError);
      }

      function onMessage() {
        finish(true);
      }

      function onClose() {
        finish(false);
      }

      function onError() {
        finish(false);
      }

      socket.addEventListener('message', onMessage);
      socket.addEventListener('close', onClose);
      socket.addEventListener('error', onError);

      try {
        this.sendJSON({});
      } catch (error) {
        finish(false);
        return;
      }

      timeout = window.setTimeout(() => finish(false), timeoutMs);
    });
  }

  public getSocket() {
    return this.socket;
  }

  public sendJSON(data: object, bypassAuth: boolean = false) {
    if (!this.socket) {
      if (Object.keys(data).length > 0 && bypassAuth === false) {
        this.pendingMessages.push(data);
      }
      return;
    }

    if (this.socket.readyState === this.socket.OPEN) {
      if (bypassAuth || this.isConnected) {
        this.socket.send(JSON.stringify(data));
      } else {
        // Queue the message until authenticated
        if (Object.keys(data).length > 0 && bypassAuth === false) { // Don't queue empty ping objects
          this.pendingMessages.push(data);
        }
      }
    } else {
      if (Object.keys(data).length > 0 && bypassAuth === false) { // Don't queue empty ping objects
        this.pendingMessages.push(data);
      }
    }
  }

  public onOpen(cb: any) {
    this.socket.onopen = cb;
  }

  public onClose(cb: (event: CloseEvent) => void) {
    this.socket.onclose = cb;
  }

  public onMessage(cb: (event: MessageEvent) => void) {
    return this.socket.onmessage = cb;
  }

  public onError(cb: (event: MessageEvent) => void) {
    this.socket.onerror = cb;
  }

}

export default WebSocketClient;



// WEBPACK FOOTER //
// ./src/clients/web-socket.client.ts